import type { UiArticle, UiBriefing, UiMetric } from "../types";

function metric(key: string, label: string, value: number, rawMatches: number, denominator: number, applicable = true): UiMetric {
  return {
    key,
    label,
    value,
    rawMatches,
    weightedMatches: rawMatches,
    denominator,
    normalization: "Demo breakdown mirrors the deterministic engine output; open raw evidence for components.",
    applicable,
  };
}

const wireArticle: UiArticle = {
  id: "article-wire-storm",
  publisherId: "wire-report",
  publisherName: "Wire Report",
  headline: "Coastal storm reaches Category 2 as shelters open",
  url: "",
  author: "Demo newsroom",
  publishedAt: "2026-08-31T10:48:00.000Z",
  section: "World",
  wordCount: 641,
  fullTextStatus: "available",
  behaviorLabels: ["HIGH FACT DENSITY"],
  profile: [
    metric("factDensity", "Fact density", 82, 12, 16),
    metric("emotionality", "Emotionality", 12, 2, 641),
    metric("headlineRhetoric", "Headline rhetoric", 8, 0, 8),
    metric("speculation", "Speculation", 18, 3, 16),
    metric("attributionQuality", "Attribution quality", 91, 1, 8),
    metric("sourceDiversity", "Source diversity", 74, 5, 8),
    metric("anonymousReliance", "Anonymous reliance", 6, 0, 8),
    metric("statisticalContext", "Statistical context", 78, 1, 5),
  ],
  headlineAudit: { finding: "No material mismatch detected", severity: "none", evidence: [] },
  languageFinding: "2 loaded terms in journalist text",
  attributionFinding: "No asymmetry detected · sample threshold met",
  loadedTerms: [
    { term: "dangerous", category: "fear", scope: "journalist", context: "conditions remained dangerous near exposed beaches", weightedValue: 2 },
    { term: "alarming", category: "fear", scope: "quote", context: "a resident called the wind alarming", weightedValue: 0.75 },
  ],
  versions: { engine: "1.1.0", lexicon: "2026.08", rules: "1.1.0" },
  rawEvidence: {
    normalizedTokens: ["coastal", "storm", "reached", "category", "2", "strength", "shelters", "opened", "three", "counties"],
    sentenceScores: [
      { sentence: "The storm reached Category 2 strength at 8 a.m. Monday.", score: 10 },
      { sentence: "Emergency shelters opened in three counties.", score: 8 },
      { sentence: "Forecasters said landfall could occur Tuesday.", score: 5 },
    ],
    ruleFirings: ["LANGUAGE-TERM-002", "STATS-001"],
  },
};

const ledgerArticle: UiArticle = {
  ...wireArticle,
  id: "article-ledger-storm",
  publisherId: "daily-ledger",
  publisherName: "The Daily Ledger",
  headline: "Coastal communities brace for powerful storm",
  profile: [
    metric("factDensity", "Fact density", 67, 9, 17),
    metric("emotionality", "Emotionality", 34, 8, 588),
    metric("headlineRhetoric", "Headline rhetoric", 28, 2, 7),
    metric("speculation", "Speculation", 31, 5, 17),
    metric("attributionQuality", "Attribution quality", 76, 2, 7),
    metric("sourceDiversity", "Source diversity", 58, 4, 7),
    metric("anonymousReliance", "Anonymous reliance", 14, 1, 7),
    metric("statisticalContext", "Statistical context", 64, 2, 6),
  ],
  headlineAudit: { finding: "Headline language is more intense than the article body", severity: "moderate", evidence: ["powerful", "brace"] },
  languageFinding: "8 loaded terms in journalist text",
};

const radioArticle: UiArticle = {
  ...wireArticle,
  id: "article-radio-storm",
  publisherId: "public-radio",
  publisherName: "Public Radio",
  headline: "Officials open shelters ahead of coastal storm",
  profile: [
    metric("factDensity", "Fact density", 79, 11, 16),
    metric("emotionality", "Emotionality", 9, 1, 702),
    metric("headlineRhetoric", "Headline rhetoric", 5, 0, 8),
    metric("speculation", "Speculation", 15, 2, 16),
    metric("attributionQuality", "Attribution quality", 88, 1, 9),
    metric("sourceDiversity", "Source diversity", 81, 6, 9),
    metric("anonymousReliance", "Anonymous reliance", 0, 0, 9),
    metric("statisticalContext", "Statistical context", 83, 1, 6),
  ],
  headlineAudit: { finding: "No material mismatch detected", severity: "none", evidence: [] },
  languageFinding: "1 loaded term in journalist text",
};

export const sampleBriefing: UiBriefing = {
  dateLabel: "Sample edition",
  generatedAt: "2026-08-31T11:15:00.000Z",
  isSample: true,
  sources: [
    { id: "wire-report", name: "Wire Report", type: "News agency", country: "US", orientation: { label: null, source: "", methodology: "", lastReviewed: "" }, ownership: "Demo source" },
    { id: "daily-ledger", name: "The Daily Ledger", type: "Newspaper", country: "US", orientation: { label: null, source: "", methodology: "", lastReviewed: "" }, ownership: "Demo source" },
    { id: "public-radio", name: "Public Radio", type: "Public broadcaster", country: "US", orientation: { label: null, source: "", methodology: "", lastReviewed: "" }, ownership: "Demo source" },
  ],
  stories: [
    {
      id: "event-coastal-storm",
      headline: "Coastal storm strengthens before landfall",
      category: "World",
      updatedLabel: "Updated 18 min ago",
      status: "HIGH CONSENSUS",
      sourceCount: 5,
      independentChainCount: 4,
      facts: [
        { text: "Storm reached Category 2 strength.", consensus: "HIGH CONSENSUS", supportedBy: ["Wire Report", "The Daily Ledger", "Public Radio"], independentChains: 3, primaryConfirmation: "Weather service advisory" },
        { text: "Landfall is forecast for Tuesday.", consensus: "MODERATE CONSENSUS", supportedBy: ["Wire Report", "Public Radio"], independentChains: 2 },
        { text: "Emergency shelters opened in three counties.", consensus: "HIGH CONSENSUS", supportedBy: ["Wire Report", "The Daily Ledger", "Public Radio"], independentChains: 3 },
      ],
      unclear: ["The exact landfall location may change."],
      disputed: [],
      coverage: [
        { publisherId: "wire-report", publisherName: "Wire Report", summary: "Forecast details 42% · Safety 31%", emphasis: [{ label: "Forecast details", percent: 42 }, { label: "Safety", percent: 31 }, { label: "Other", percent: 27 }] },
        { publisherId: "daily-ledger", publisherName: "The Daily Ledger", summary: "Regional impact 47%", emphasis: [{ label: "Regional impact", percent: 47 }, { label: "Forecast", percent: 28 }, { label: "Other", percent: 25 }] },
        { publisherId: "public-radio", publisherName: "Public Radio", summary: "Emergency response 38%", emphasis: [{ label: "Emergency response", percent: 38 }, { label: "Safety", percent: 35 }, { label: "Other", percent: 27 }] },
      ],
      contrast: {
        publisherA: "wire-report",
        publisherB: "daily-ledger",
        publisherNameA: "Wire Report",
        publisherNameB: "The Daily Ledger",
        score: 31,
        frameA: "Forecast details",
        frameB: "Regional impact",
        dimensions: [
          { metric: "emotionality", label: "Emotionality", valueA: 12, valueB: 34, difference: 22 },
          { metric: "factDensity", label: "Fact density", valueA: 82, valueB: 67, difference: 15 }
        ]
      },
      terminology: [{ left: "storm", right: "hurricane" }, { left: "evacuation order", right: "emergency guidance" }],
      primarySources: [{ title: "Weather service advisory", url: "https://www.weather.gov/" }],
      articles: [wireArticle, ledgerArticle, radioArticle],
    },
    {
      id: "event-chip-plant",
      headline: "Chipmaker announces new fabrication plant",
      category: "Technology",
      updatedLabel: "Updated 42 min ago",
      status: "DEVELOPING",
      sourceCount: 3,
      independentChainCount: 2,
      facts: [{ text: "The company announced a new fabrication plant.", consensus: "ATTRIBUTED", supportedBy: ["Company filing", "Wire Report"], independentChains: 1 }],
      unclear: ["Construction timing has not been independently confirmed."],
      disputed: [],
      coverage: [], contrast: null, terminology: [], primarySources: [], articles: [],
    },
    {
      id: "event-battery-study",
      headline: "Researchers publish battery recycling results",
      category: "Science",
      updatedLabel: "Updated 1 hr ago",
      status: "MODERATE CONSENSUS",
      sourceCount: 4,
      independentChainCount: 3,
      facts: [{ text: "The study reports improved material recovery in a laboratory trial.", consensus: "MODERATE CONSENSUS", supportedBy: ["Journal paper", "Wire Report"], independentChains: 2 }],
      unclear: ["Commercial-scale results are not yet available."],
      disputed: [],
      coverage: [], contrast: null, terminology: [], primarySources: [], articles: [],
    },
  ],
};
